/**
 * 変数バリデーター
 *
 * シナリオで定義された変数の初期化とバリデーションを行います。
 */

import type { VariableDefinition, VariableMutation } from 'shared';
import {
  validateVariableType,
  clampVariableValue,
  applyVariableMutation as applyMutation,
} from 'shared';

/**
 * バリデーション結果
 */
export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * 変数バリデータークラス
 */
export class VariableValidator {
  private definitions: VariableDefinition[];

  constructor(definitions: VariableDefinition[] = []) {
    this.definitions = definitions;
  }

  /**
   * 変数を初期化（デフォルト値を適用）
   */
  initializeVariables(existing: Record<string, unknown> = {}): Record<string, unknown> {
    const result = { ...existing };
    for (const def of this.definitions) {
      if (!(def.key in result)) {
        result[def.key] = def.defaultValue;
      }
    }
    return result;
  }

  /**
   * 変数の型と制約をバリデート
   */
  validate(variables: Record<string, unknown>): ValidationResult {
    const errors: string[] = [];
    const warnings: string[] = [];

    for (const def of this.definitions) {
      const val = variables[def.key];

      // 未定義チェック
      if (val === undefined) {
        warnings.push(`${def.key}: 未定義（デフォルト値を使用）`);
        continue;
      }

      // 型チェック
      if (!validateVariableType(val, def.type)) {
        errors.push(`${def.key}: 型エラー - 期待: ${def.type}, 実際: ${typeof val}`);
        continue;
      }

      // 数値制約チェック
      if (def.type === 'number') {
        const numVal = val as number;
        if (def.min !== undefined && numVal < def.min) {
          errors.push(`${def.key}: 値 ${numVal} が最小値 ${def.min} を下回っています`);
        }
        if (def.max !== undefined && numVal > def.max) {
          errors.push(`${def.key}: 値 ${numVal} が最大値 ${def.max} を超えています`);
        }
      }
    }

    return {
      valid: errors.length === 0,
      errors,
      warnings,
    };
  }

  /**
   * 変数の定義を取得
   */
  getDefinition(key: string): VariableDefinition | undefined {
    return this.definitions.find((d) => d.key === key);
  }

  /**
   * 全定義を取得
   */
  getDefinitions(): VariableDefinition[] {
    return this.definitions;
  }

  /**
   * デバッグ表示対象の変数を取得
   */
  getDebugVariables(variables: Record<string, unknown>): Array<{
    key: string;
    value: unknown;
    label?: string;
    type: string;
    min?: number;
    max?: number;
    icon?: string;
    color?: string;
  }> {
    return this.definitions
      .filter((def) => def.showInDebug !== false)
      .map((def) => ({
        key: def.key,
        value: variables[def.key] ?? def.defaultValue,
        label: def.label,
        type: def.type,
        min: def.min,
        max: def.max,
        icon: def.icon,
        color: def.color,
      }));
  }

  /**
   * 変数操作を適用
   */
  applyMutation(
    variables: Record<string, unknown>,
    mutation: VariableMutation
  ): Record<string, unknown> {
    return applyMutation(variables, mutation, this.definitions);
  }

  /**
   * 複数の変数操作を適用
   */
  applyMutations(
    variables: Record<string, unknown>,
    mutations: VariableMutation[]
  ): Record<string, unknown> {
    let result = { ...variables };
    for (const mutation of mutations) {
      result = this.applyMutation(result, mutation);
    }
    return result;
  }

  /**
   * 変数値をクランプ（範囲内に収める）
   */
  clampValue(key: string, value: number): number {
    const def = this.getDefinition(key);
    if (!def || def.type !== 'number') {
      return value;
    }
    return clampVariableValue(value, def.min, def.max);
  }

  /**
   * 変数が閾値条件を満たすかチェック
   * Phase A-h のエンディング条件判定で使用
   */
  checkThreshold(
    variables: Record<string, unknown>,
    key: string,
    operator: 'eq' | 'ne' | 'lt' | 'lte' | 'gt' | 'gte',
    threshold: number
  ): boolean {
    const value = Number(variables[key] ?? 0);
    if (Number.isNaN(value)) {
      return false;
    }

    switch (operator) {
      case 'eq':
        return value === threshold;
      case 'ne':
        return value !== threshold;
      case 'lt':
        return value < threshold;
      case 'lte':
        return value <= threshold;
      case 'gt':
        return value > threshold;
      case 'gte':
        return value >= threshold;
      default:
        return false;
    }
  }
}

/**
 * 変数バリデーターを作成
 */
export function createVariableValidator(
  definitions: VariableDefinition[] = []
): VariableValidator {
  return new VariableValidator(definitions);
}
